/**
 * KinsCircle Intelligence Layer V1 — Tree Health Monitor
 *
 * Pure, deterministic, read-only heuristic engine that audits a loaded
 * family graph and surfaces issues for owner / admin review.
 *
 * V1 IS REPORT-ONLY:
 *   - Returns a list of TreeIssue objects.
 *   - NEVER auto-repairs.
 *   - NEVER auto-merges.
 *   - NEVER auto-deletes.
 *   - NEVER mutates parent_ids, spouse_id, relationship_override, or any
 *     other column.
 *   - NEVER calls Supabase.
 *   - NEVER touches the spouse / in-law engine, the tree layout, the
 *     relationship labels, billing, uploads, auth, schema, or homepage.
 *
 * Designed to run in the browser against the already-loaded `members`
 * array from FamilyGraphContext. Cheap enough to run on demand from the
 * admin "Run Tree Intelligence Check" button.
 *
 * Honors STABILITY.md §1 (locked subsystems), §3 (additive only), §7
 * (production stability lock — no blank pages, internal-only logging),
 * and §8 (no destructive changes).
 */

import type { GraphMember } from '@/contexts/FamilyGraphContext';

export type IssueSeverity = 'low' | 'medium' | 'high';

export type IssueCategory =
  | 'disconnected'
  | 'needs-connection'
  | 'orphan-spouse'
  | 'duplicate-likely'
  | 'missing-anchor'
  | 'relationship-loop'
  | 'parent-cycle'
  | 'invited-not-bound'
  | 'missing-birthday'
  | 'orphan-upload'
  | 'self-as-parent'
  | 'spouse-as-parent';

export interface TreeIssue {
  id: string;
  category: IssueCategory;
  title: string;
  severity: IssueSeverity;
  /** Member ids the issue affects (zero or more). */
  affectedMemberIds: string[];
  /** Display names of the affected members, for UI rendering. */
  affectedNames: string[];
  /** A short, plain-English explanation suitable for owners / admins. */
  likelyCause: string;
  /** A short, action-oriented next step. NEVER auto-runs in V1. */
  recommendedNextStep: string;
  /**
   * 'safe' = caller can offer a manual repair UI later (still gated by
   *           explicit confirmation per the V1 contract).
   * 'manual' = MUST be resolved by the owner from the existing UI; no
   *           automated repair is appropriate.
   */
  safeOrManual: 'safe' | 'manual';

  /**
   * Optional live repair metadata.
   * Present when the intelligence layer can build a repair proposal.
   */
  repairable?: boolean;
  repairProposalId?: string | null;
  repairRisk?: 'low' | 'medium' | 'high';
  autoRepairEligible?: boolean;
}

export interface TreeHealthReport {
  generatedAt: string;
  memberCount: number;
  /** A short narrative summary suitable for the top of the report card. */
  summary: string;
  /** All issues found, sorted by severity desc then by category. */
  issues: TreeIssue[];
  /** Quick severity counts. */
  counts: Record<IssueSeverity, number>;
}

const norm = (s: string | null | undefined): string =>
  (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const strictTokens = (s: string | null | undefined): string[] =>
  norm(s).split(' ').filter((t) => t.length >= 2);

/**
 * Run the V1 rule-based tree health check.
 *
 * @param members - the already-loaded family graph (from FamilyGraphContext).
 * @param viewerMemberId - the row id representing the current viewer (optional).
 * @returns a structured report, never throws.
 */
export const runTreeHealthCheck = (
  members: GraphMember[],
  viewerMemberId?: string | null,
): TreeHealthReport => {
  const issues: TreeIssue[] = [];
  const byId = new Map(members.map((m) => [m.id, m]));
  const active = members.filter((m) => m.approvalStatus !== 'rejected');

  const pushIssue = (issue: TreeIssue) => {
    const repairable = issue.safeOrManual === 'safe';
    issues.push({
      ...issue,
      repairable,
      repairProposalId: repairable ? `repair-${issue.id}` : null,
      repairRisk: issue.severity === 'high' ? 'high' : issue.severity === 'medium' ? 'medium' : 'low',
      autoRepairEligible: repairable && issue.severity !== 'high',
    });
  };

  // ── 1. Self-as-parent guard ───────────────────────────────────────────
  // A member's parent_ids must not contain their own id. If we see this
  // it's almost certainly a stale write from a deprecated code path and
  // should be flagged, never auto-repaired.
  for (const m of active) {
    if ((m.parentIds ?? []).includes(m.id)) {
      pushIssue({
        id: `self-parent-${m.id}`,
        category: 'self-as-parent',
        title: `${m.name} is listed as their own parent`,
        severity: 'high',
        affectedMemberIds: [m.id],
        affectedNames: [m.name],
        likelyCause:
          'A previous save accidentally included this member in their own parent_ids list.',
        recommendedNextStep:
          'Open this profile in the Family Tree, choose Edit, and remove the self-reference from parent links.',
        safeOrManual: 'manual',
      });
    }
  }

  // ── 2. Spouse-as-parent guard ─────────────────────────────────────────
  // The locked engine forbids spouse_id and parent_ids overlapping on the
  // same row. We never auto-fix; we only report so the operator can see it.
  for (const m of active) {
    if (!m.spouseId) continue;
    if ((m.parentIds ?? []).includes(m.spouseId)) {
      const spouse = byId.get(m.spouseId);
      pushIssue({
        id: `spouse-parent-${m.id}`,
        category: 'spouse-as-parent',
        title: `${m.name}'s spouse is also listed as a parent`,
        severity: 'high',
        affectedMemberIds: spouse ? [m.id, spouse.id] : [m.id],
        affectedNames: spouse ? [m.name, spouse.name] : [m.name],
        likelyCause:
          'The same person appears in both spouse_id and parent_ids on this row, which is structurally impossible.',
        recommendedNextStep:
          'Decide whether the relationship is a parent or a spouse, then remove the incorrect link from the Edit dialog.',
        safeOrManual: 'manual',
      });
    }
  }

  // ── 3. Parent cycle / loop detection ──────────────────────────────────
  // Walk parent_ids upward from each member; if we revisit the start id
  // we've found a cycle. We don't try to localize the exact cycle path —
  // surfacing the entry member is enough for the operator to investigate.
  const seenInCycleReport = new Set<string>();
  for (const m of active) {
    if (seenInCycleReport.has(m.id)) continue;
    const visited = new Set<string>();
    const stack: string[] = [...(m.parentIds ?? [])];
    let cycleHit = false;
    while (stack.length) {
      const cur = stack.pop()!;
      if (cur === m.id) {
        cycleHit = true;
        break;
      }
      if (visited.has(cur)) continue;
      visited.add(cur);
      const p = byId.get(cur);
      if (!p) continue;
      for (const pid of p.parentIds ?? []) {
        if (!visited.has(pid)) stack.push(pid);
      }
    }
    if (cycleHit) {
      seenInCycleReport.add(m.id);
      pushIssue({
        id: `cycle-${m.id}`,
        category: 'parent-cycle',
        title: `Parent loop detected starting from ${m.name}`,
        severity: 'high',
        affectedMemberIds: [m.id],
        affectedNames: [m.name],
        likelyCause:
          'Following parent_ids upward from this member eventually leads back to themselves, which means there is a circular ancestry chain.',
        recommendedNextStep:
          'Audit this branch in the Family Tree and remove the parent link that closes the loop. Never auto-repair — pick the side to keep.',
        safeOrManual: 'manual',
      });
    }
  }

  // ── 4. Orphan spouse — spouse_id points at a non-existent row ────────
  for (const m of active) {
    if (!m.spouseId) continue;
    if (!byId.has(m.spouseId)) {
      pushIssue({
        id: `orphan-spouse-${m.id}`,
        category: 'orphan-spouse',
        title: `${m.name}'s spouse link points to a deleted relative`,
        severity: 'medium',
        affectedMemberIds: [m.id],
        affectedNames: [m.name],
        likelyCause:
          'spouse_id references a member id that no longer exists in this family graph (typically because the spouse row was deleted).',
        recommendedNextStep:
          'Open this profile and either re-link the spouse to an existing relative or clear the spouse field.',
        safeOrManual: 'safe',
      });
    }
  }

  // ── 5. Missing anchor — parent_ids reference a non-existent row ──────
  for (const m of active) {
    const dangling = (m.parentIds ?? []).filter((pid) => pid && !byId.has(pid));
    if (dangling.length === 0) continue;
    pushIssue({
      id: `missing-anchor-${m.id}`,
      category: 'missing-anchor',
      title: `${m.name} has ${dangling.length} parent link${dangling.length > 1 ? 's' : ''} pointing to deleted relatives`,
      severity: 'medium',
      affectedMemberIds: [m.id],
      affectedNames: [m.name],
      likelyCause:
        'parent_ids references one or more member ids that no longer exist in this family graph.',
      recommendedNextStep:
        'Open this profile and re-link the parent to an existing relative, or clear the dangling parent reference.',
      safeOrManual: 'safe',
    });
  }

  // ── 6. Disconnected members — no parents, no children, no spouse ─────
  // Use childrenOf reverse index for accurate detection.
  const childrenOf = new Map<string, string[]>();
  for (const m of active) {
    for (const pid of m.parentIds ?? []) {
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid)!.push(m.id);
    }
  }
  for (const m of active) {
    if (m.id === viewerMemberId) continue; // viewer is the implicit anchor
    if (m.side === 'self' && (m.relationship as string) === 'unknown') continue;
    const hasParents = (m.parentIds ?? []).filter(Boolean).length > 0;
    const hasChildren = (childrenOf.get(m.id) ?? []).length > 0;
    const hasSpouse = !!m.spouseId && byId.has(m.spouseId);
    if (!hasParents && !hasChildren && !hasSpouse) {
      pushIssue({
        id: `disconnected-${m.id}`,
        category: 'disconnected',
        title: `${m.name} is not connected to anyone in the tree`,
        severity: 'low',
        affectedMemberIds: [m.id],
        affectedNames: [m.name],
        likelyCause:
          'This member has no parents, no children, and no spouse linked, so they sit on their own in the family graph.',
        recommendedNextStep:
          'Open this profile and link them to a parent, child, or spouse so they appear in the connected tree.',
        safeOrManual: 'safe',
      });
    }
  }

  // ── 7. Members stuck in "Needs Connection" (pending approval) ────────
  for (const m of active) {
    if (m.approvalStatus === 'pending') {
      pushIssue({
        id: `pending-${m.id}`,
        category: 'needs-connection',
        title: `${m.name} is waiting for approval`,
        severity: 'low',
        affectedMemberIds: [m.id],
        affectedNames: [m.name],
        likelyCause:
          'This member was added by a non-staff user and is still in the pending approval queue.',
        recommendedNextStep:
          'Review the pending approvals panel and approve or reject this member.',
        safeOrManual: 'safe',
      });
    }
  }

  // ── 8. Likely duplicate people ───────────────────────────────────────
  // Pairwise comparison using the same strict tokenizer as the locked
  // findPossibleDuplicates engine — first AND last name token must agree,
  // single-character middle initials are ignored. We additionally require
  // EITHER a birth-year match (within 3 years) OR same-side membership to
  // avoid flagging unrelated namesakes.
  const seenPairs = new Set<string>();
  for (let i = 0; i < active.length; i += 1) {
    for (let j = i + 1; j < active.length; j += 1) {
      const a = active[i];
      const b = active[j];
      const aTokens = strictTokens(a.name);
      const bTokens = strictTokens(b.name);
      if (aTokens.length < 2 || bTokens.length < 2) continue;
      const firstMatch = aTokens[0] === bTokens[0];
      const lastMatch = aTokens[aTokens.length - 1] === bTokens[bTokens.length - 1];
      if (!firstMatch || !lastMatch) continue;
      const sameSide =
        a.side === b.side ||
        a.side === 'both' ||
        b.side === 'both' ||
        a.side === 'self' ||
        b.side === 'self';
      const yearMatch =
        !!a.birthYear &&
        !!b.birthYear &&
        Math.abs(a.birthYear - b.birthYear) <= 3;
      const exactDOB =
        !!a.birthDate && !!b.birthDate && a.birthDate === b.birthDate;
      if (!sameSide && !yearMatch && !exactDOB) continue;
      // Don't flag a row against itself or its known spouse — spouses
      // sometimes share a last name and should not be reported.
      if (a.spouseId === b.id || b.spouseId === a.id) continue;
      const pairKey = [a.id, b.id].sort().join('|');
      if (seenPairs.has(pairKey)) continue;
      seenPairs.add(pairKey);
      pushIssue({
        id: `duplicate-${pairKey}`,
        category: 'duplicate-likely',
        title: `${a.name} and ${b.name} may be the same person`,
        severity: exactDOB ? 'high' : yearMatch ? 'medium' : 'low',
        affectedMemberIds: [a.id, b.id],
        affectedNames: [a.name, b.name],
        likelyCause: exactDOB
          ? 'Both members share the same first name, last name, and exact date of birth.'
          : yearMatch
          ? 'Both members share the same first and last name, and their birth years are within three years of each other.'
          : 'Both members share the same first and last name on the same side of the family.',
        recommendedNextStep:
          'Review both profiles. If they are the same person, use the existing duplicate-merge flow — V1 never merges automatically.',
        safeOrManual: 'manual',
      });
    }
  }

  // ── 9. Members with missing birthdays ────────────────────────────────
  // Low-severity housekeeping signal — many trees ship without DOBs and
  // that's fine. We surface this only as a count-style nudge to populate
  // upcoming-birthday reminders.
  const missingBirthday = active.filter(
    (m) =>
      !m.birthDate &&
      !(m.birthMonth && m.birthDay) &&
      !m.birthYear &&
      m.livingStatus !== 'deceased',
  );
  if (missingBirthday.length > 0) {
    pushIssue({
      id: 'missing-birthday-summary',
      category: 'missing-birthday',
      title: `${missingBirthday.length} relative${missingBirthday.length > 1 ? 's are' : ' is'} missing a birthday`,
      severity: 'low',
      affectedMemberIds: missingBirthday.slice(0, 8).map((m) => m.id),
      affectedNames: missingBirthday.slice(0, 8).map((m) => m.name),
      likelyCause:
        'These members have no birth date or birth month + day saved, so they will not appear in birthday reminders.',
      recommendedNextStep:
        'Open each profile and add a birthday when you know it. Optional — leaving them blank is fine.',
      safeOrManual: 'safe',
    });
  }

  // ── Sort & summarize ─────────────────────────────────────────────────
  const severityOrder: Record<IssueSeverity, number> = { high: 0, medium: 1, low: 2 };
  issues.sort((a, b) => {
    const s = severityOrder[a.severity] - severityOrder[b.severity];
    if (s !== 0) return s;
    return a.category.localeCompare(b.category);
  });

  const counts: Record<IssueSeverity, number> = { high: 0, medium: 0, low: 0 };
  for (const i of issues) counts[i.severity] += 1;

  const summary =
    issues.length === 0
      ? `Looks good — ${active.length} relatives, no structural issues found.`
      : `${issues.length} issue${issues.length === 1 ? '' : 's'} found across ${active.length} relatives. ${counts.high} high · ${counts.medium} medium · ${counts.low} low.`;

  return {
    generatedAt: new Date().toISOString(),
    memberCount: active.length,
    summary,
    issues,
    counts,
  };
};
		
